/**
 * Opnemen in de browser, als WAV (16 kHz, mono, 16 bits): de spraakproef
 * (stap 28). Een browser neemt met MediaRecorder op als webm (Chrome) of mp4
 * (Safari); dat zou de server eerst moeten omzetten. Daarom hier de ruwe
 * klank uit de microfoon (Web Audio), omgezet naar 16 kHz, als WAV-bestand.
 *
 *   const opname = await begin();       // vraagt om de microfoon
 *   const blob = await opname.stop();   // de WAV, als Blob
 *
 * herbemonster() en maakWav() staan los, zodat de tests ze kunnen nakijken.
 */
export const FREQUENTIE = 16000;

/** Van de frequentie van de microfoon naar 16 kHz: het gemiddelde van elk blokje. */
export function herbemonster(monsters, van, naar = FREQUENTIE) {
  if (van === naar) return monsters;
  const verhouding = van / naar;
  const lengte = Math.floor(monsters.length / verhouding);
  const uit = new Float32Array(lengte);
  for (let i = 0; i < lengte; i++) {
    const begin = Math.floor(i * verhouding);
    const eind = Math.min(monsters.length, Math.floor((i + 1) * verhouding));
    let som = 0;
    for (let j = begin; j < eind; j++) som += monsters[j];
    uit[i] = eind > begin ? som / (eind - begin) : 0;
  }
  return uit;
}

/** Een WAV-bestand (PCM, 16 bits, mono) van monsters tussen -1 en 1. */
export function maakWav(monsters, frequentie = FREQUENTIE) {
  const buffer = new ArrayBuffer(44 + monsters.length * 2);
  const zicht = new DataView(buffer);
  const tekst = (plek, s) => { for (let i = 0; i < s.length; i++) zicht.setUint8(plek + i, s.charCodeAt(i)); };
  tekst(0, 'RIFF');
  zicht.setUint32(4, 36 + monsters.length * 2, true);
  tekst(8, 'WAVE');
  tekst(12, 'fmt ');
  zicht.setUint32(16, 16, true);          // grootte van het fmt-blok
  zicht.setUint16(20, 1, true);           // PCM
  zicht.setUint16(22, 1, true);           // mono
  zicht.setUint32(24, frequentie, true);
  zicht.setUint32(28, frequentie * 2, true);
  zicht.setUint16(32, 2, true);
  zicht.setUint16(34, 16, true);
  tekst(36, 'data');
  zicht.setUint32(40, monsters.length * 2, true);
  for (let i = 0; i < monsters.length; i++) {
    const m = Math.max(-1, Math.min(1, monsters[i]));
    zicht.setInt16(44 + i * 2, m < 0 ? m * 0x8000 : m * 0x7fff, true);
  }
  return buffer;
}

/** Begin met opnemen; geeft { stop } terug. Een weigering van de microfoon komt als fout. */
export async function begin() {
  const stroom = await navigator.mediaDevices.getUserMedia({ audio: { channelCount: 1, echoCancellation: true } });
  const Context = window.AudioContext || window.webkitAudioContext;
  const context = new Context();
  const bron = context.createMediaStreamSource(stroom);
  // ScriptProcessor is oud, maar werkt in elke browser en is voor een paar seconden ruim genoeg.
  const verwerker = context.createScriptProcessor(4096, 1, 1);
  const stukken = [];
  verwerker.onaudioprocess = (e) => stukken.push(new Float32Array(e.inputBuffer.getChannelData(0)));
  bron.connect(verwerker);
  verwerker.connect(context.destination);
  return {
    stop: async () => {
      verwerker.disconnect();
      bron.disconnect();
      stroom.getTracks().forEach(t => t.stop());
      const lengte = stukken.reduce((n, s) => n + s.length, 0);
      const alles = new Float32Array(lengte);
      let plek = 0;
      stukken.forEach(s => { alles.set(s, plek); plek += s.length; });
      const frequentie = context.sampleRate;
      await context.close();
      return new Blob([maakWav(herbemonster(alles, frequentie))], { type: 'audio/wav' });
    },
  };
}
